import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Calendar as CalendarIcon, Home, Store, CheckCircle, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import BottomNavigation from "@/components/BottomNavigation";
import { ScreenHeader, FormField as DesignFormField, MonoValue } from "@/components/redesign";

interface Project {
  id: string;
  name: string;
  details?: {
    flats?: number;
    shops?: number;
    units?: Array<{
      type: 'flat' | 'shop';
      number: number;
      area: number;
    }>;
  };
}

interface Unit {
  type: 'flat' | 'shop';
  number: number;
  area: number;
}

const SalePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { id } = useParams();
  const { t } = useTranslation();

  // State
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [saleAmount, setSaleAmount] = useState<string>("");
  const [currency, setCurrency] = useState<string>("TRY");
  const [date, setDate] = useState<Date>(new Date());
  const [description, setDescription] = useState<string>("");
  const [buyerName, setBuyerName] = useState<string>("");
  const [buyerPhone, setBuyerPhone] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [soldUnits, setSoldUnits] = useState<Array<{projectId: string, unitType: string, unitNumber: number}>>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [originalSaleEntry, setOriginalSaleEntry] = useState<any>(null);

  // Fetch projects on component mount
  useEffect(() => {
    fetchProjects();
    fetchSoldUnits();
    
    // If there's an ID in the URL, we're editing an existing sale
    if (id) {
      setIsEditing(true);
    }
  }, [id]);

  // Load existing sale data after projects are loaded
  useEffect(() => {
    if (id && projects.length > 0 && !loadingProjects) {
      loadExistingSale(id);
    }
  }, [id, projects, loadingProjects]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, details')
        .eq('user_id', user.id);

      if (error) throw error;

      setProjects(data || []);
    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('sales.errors.projects_load_error'),
        variant: "destructive",
      });
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchSoldUnits = async () => {
    try {
      // Fetch existing sales to prevent duplicate sales
      const { data, error } = await supabase
        .from('entries')
        .select('description, project_id')
        .eq('type', 'sale')
        .eq('user_id', user.id);

      if (error) throw error;

      // Parse sold units from entry descriptions
      // Support both old format (hardcoded Turkish) and new format (with translations)
      const soldUnitsData = data
        .map(entry => {
          // Extract unit info from description - try both Turkish and translated versions
          const flatMatchTurkish = entry.description.match(/Daire (\d+) Satışı/);
          const shopMatchTurkish = entry.description.match(/Dükkan (\d+) Satışı/);
          
          // Also try to match with current translations
          const flatText = t('sales.unit_type.flat');
          const shopText = t('sales.unit_type.shop');
          const flatMatchTranslated = entry.description.match(new RegExp(`${flatText} (\\d+) Satışı`));
          const shopMatchTranslated = entry.description.match(new RegExp(`${shopText} (\\d+) Satışı`));
          
          if (flatMatchTurkish || flatMatchTranslated) {
            const number = flatMatchTurkish ? parseInt(flatMatchTurkish[1]) : parseInt(flatMatchTranslated![1]);
            return {
              projectId: entry.project_id,
              unitType: 'flat',
              unitNumber: number
            };
          } else if (shopMatchTurkish || shopMatchTranslated) {
            const number = shopMatchTurkish ? parseInt(shopMatchTurkish[1]) : parseInt(shopMatchTranslated![1]);
            return {
              projectId: entry.project_id,
              unitType: 'shop',
              unitNumber: number
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{projectId: string, unitType: string, unitNumber: number}>;

      setSoldUnits(soldUnitsData);
    } catch (error) {
      
    }
  };

  // Function to load existing sale data for editing
  const loadExistingSale = async (saleId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .eq('id', saleId)
        .eq('type', 'sale')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setOriginalSaleEntry(data);
        
        // Parse metric_details to get sale information
        let metricData: any = {};
        if (data.metric_details) {
          try {
            metricData = typeof data.metric_details === 'string' 
              ? JSON.parse(data.metric_details) 
              : data.metric_details;
          } catch (e) {
            
          }
        }

        // Set form data from the existing sale
        setSelectedProjectId(data.project_id);
        
        // Find the project to set it properly
        const project = projects.find(p => p.id === data.project_id);
        if (project) {
          setSelectedProject(project);
        }

        // Set unit information from metric_details
        if (metricData?.unitType && metricData?.unitNumber) {
          setSelectedUnit({
            type: metricData.unitType,
            number: metricData.unitNumber,
            area: metricData.unitArea || 0
          });
        }

        // Set other form fields
        setSaleAmount(data.original_amount?.toString() || '');
        setCurrency(data.original_currency || 'TRY');
        setDate(new Date(data.date));
        setDescription(metricData?.customDescription || '');
        setBuyerName(metricData?.buyerName || '');
        setBuyerPhone(metricData?.buyerPhone || '');
      }
    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('sales.errors.sale_load_error'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle project selection
  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    const project = projects.find(p => p.id === projectId);
    setSelectedProject(project || null);
    setSelectedUnit(null); // Reset unit selection
  };

  // Check if a unit is already sold
  const isUnitSold = (unit: Unit) => {
    return soldUnits.some(soldUnit => {
      const isSameUnit = soldUnit.projectId === selectedProjectId &&
                        soldUnit.unitType === unit.type &&
                        soldUnit.unitNumber === unit.number;
      
      // If we're editing and this is the same unit we're editing, it's not considered "sold"
      if (isEditing && selectedUnit && 
          soldUnit.unitType === selectedUnit.type && 
          soldUnit.unitNumber === selectedUnit.number &&
          soldUnit.projectId === selectedProjectId) {
        return false;
      }
      
      return isSameUnit;
    });
  };

  // Get available units for the selected project
  const getAvailableUnits = (): Unit[] => {
    if (!selectedProject?.details) return [];
    
    let units: Unit[] = [];
    
    // If units array exists and has content, use it
    if (selectedProject.details.units && selectedProject.details.units.length > 0) {
      units = selectedProject.details.units;
    } else {
      // Generate units dynamically based on flats and shops counts
      const { flats = 0, shops = 0 } = selectedProject.details;
      
      // Generate flats
      for (let i = 1; i <= flats; i++) {
        units.push({
          type: 'flat',
          number: i,
          area: 0 // Default area when not specified
        });
      }
      
      // Generate shops
      for (let i = 1; i <= shops; i++) {
        units.push({
          type: 'shop',
          number: i,
          area: 0 // Default area when not specified
        });
      }
    }
    
    // Filter out sold units
    return units.filter(unit => !isUnitSold(unit));
  };

  // Handle sale submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProjectId) {
      toast({
        title: t('common.error'),
        description: t('sales.errors.project_required'),
        variant: "destructive",
      });
      return;
    }

    if (!selectedUnit) {
      toast({
        title: t('common.error'),
        description: t('sales.errors.unit_required'),
        variant: "destructive",
      });
      return;
    }

    if (!saleAmount || isNaN(parseFloat(saleAmount))) {
      toast({
        title: t('common.error'),
        description: t('sales.errors.amount_invalid'),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Create the sale description first
      const unitTypeText = selectedUnit.type === 'flat' ? t('sales.unit_type.flat') : t('sales.unit_type.shop');
      const saleDescriptionBase = selectedUnit.type === 'flat' 
        ? t('sales.sale_description.flat_sale', { number: selectedUnit.number })
        : t('sales.sale_description.shop_sale', { number: selectedUnit.number });
      const buyerPart = buyerName ? t('sales.sale_description.with_buyer', { buyerName }) : '';
      const descriptionPart = description ? t('sales.sale_description.with_description', { description }) : '';
      const fullDescription = `${saleDescriptionBase}${buyerPart}${descriptionPart}`;

      // Check once more if the unit is still available (check for base description pattern)
      const { data: existingEntries, error: checkError } = await supabase
        .from('entries')
        .select('id')
        .eq('type', 'sale')
        .eq('project_id', selectedProjectId)
        .eq('user_id', user.id)
        .like('description', `${saleDescriptionBase}%`);

      if (checkError) throw checkError;

      // Skip availability check if we're editing the same sale
      if (existingEntries && existingEntries.length > 0 && !isEditing) {
        toast({
          title: t('common.error'),
          description: t('sales.errors.unit_already_sold'),
          variant: "destructive",
        });
        return;
      }

      // If editing, make sure we're not conflicting with another sale (other than our own)
      if (isEditing && existingEntries && existingEntries.length > 0) {
        const conflictingSale = existingEntries.find(entry => entry.id !== originalSaleEntry?.id);
        if (conflictingSale) {
          toast({
            title: t('common.error'),
            description: t('sales.errors.unit_conflict'),
            variant: "destructive",
          });
          return;
        }
      }
      
              const saleData = {
          type: 'sale',
          project_id: selectedProjectId,
          user_id: user.id,
          date: date.toISOString(),
          description: fullDescription,
        original_amount: parseFloat(saleAmount),
        original_currency: currency,
        is_official: true,
        has_invoice: false,
        // Store unit details in metric_details for future reference
        metric_details: JSON.stringify({
          unitType: selectedUnit.type,
          unitNumber: selectedUnit.number,
          unitArea: selectedUnit.area,
          saleAmount: parseFloat(saleAmount),
          currency: currency,
          customDescription: description,
          buyerName: buyerName,
          buyerPhone: buyerPhone
        })
      };

      if (isEditing && originalSaleEntry) {
        // Update existing sale
        const { error } = await supabase
          .from('entries')
          .update(saleData)
          .eq('id', originalSaleEntry.id);

        if (error) throw error;

        toast({
          title: t('common.success'),
          description: t('sales.success.sale_updated', { 
            unitType: unitTypeText, 
            unitNumber: selectedUnit.number 
          }),
        });
      } else {
        // Create new sale
        const { error } = await supabase.from('entries').insert([saleData]);

        if (error) throw error;

        toast({
          title: t('common.success'),
          description: t('sales.success.sale_saved', { 
            unitType: unitTypeText, 
            unitNumber: selectedUnit.number 
          }),
        });
      }

      // Navigate back to home or project detail
      if (isEditing && selectedProjectId) {
        navigate(`/project/${selectedProjectId}`);
      } else {
        navigate('/');
      }

    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('sales.errors.sale_save_error'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const currencyOptions = [
    { value: "TRY", label: "TRY" },
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
  ];

  return (
    <div className="min-h-screen bg-gray-25 flex flex-col">
      <ScreenHeader
        title={isEditing ? t('sales.edit_title') : t('sales.title')}
        onBack={() => navigate('/')}
      />

      {/* Main scrollable content area with improved mobile scrolling */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden mobile-scroll-container">
          <div className="pt-4 pb-24 px-4 min-h-full">
            <div className="max-w-md mx-auto space-y-4">

              {/* Sale Form Card */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="h-5 w-5 text-brand-600" />
                  <h2 className="text-[18px] font-bold text-brand-900">{t('sales.unit_sale')}</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Project Selection */}
                  <DesignFormField label={t('sales.project')} required>
                    <Select
                      value={selectedProjectId}
                      onValueChange={handleProjectChange}
                      disabled={loadingProjects}
                    >
                      <SelectTrigger className="bg-white border-gray-200 text-gray-900 rounded-md">
                        <SelectValue placeholder={loadingProjects ? t('sales.projects_loading') : t('sales.select_project')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DesignFormField>

                  {/* Unit Selection */}
                  {selectedProject && (
                    <DesignFormField label={t('sales.unit')} required>
                      {getAvailableUnits().length === 0 ? (
                        <div className="text-sm text-gray-500 p-3 border border-gray-200 rounded-md bg-gray-50">
                          {t('sales.no_available_units')}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-2 mt-1 max-h-64 overflow-y-auto mobile-scroll-container">
                          {getAvailableUnits().map((unit) => {
                            const isSelected = selectedUnit?.type === unit.type && selectedUnit?.number === unit.number;
                            const isFlat = unit.type === 'flat';
                            return (
                              <button
                                key={`${unit.type}-${unit.number}`}
                                type="button"
                                className={`p-3 border rounded-xl transition-colors mobile-touch-friendly w-full text-left ${
                                  isSelected
                                    ? isFlat
                                      ? 'border-brand-400 bg-brand-50'
                                      : 'border-warning-300 bg-warning-50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => setSelectedUnit(unit)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {isFlat ? (
                                      <Home className={`h-4 w-4 ${isSelected ? 'text-brand-600' : 'text-gray-400'}`} />
                                    ) : (
                                      <Store className={`h-4 w-4 ${isSelected ? 'text-warning-600' : 'text-gray-400'}`} />
                                    )}
                                    <span className={`font-medium text-sm ${isSelected ? (isFlat ? 'text-brand-700' : 'text-warning-700') : 'text-gray-700'}`}>
                                      {isFlat ? t('sales.unit_type.flat') : t('sales.unit_type.shop')} {unit.number}
                                    </span>
                                  </div>
                                  {unit.area > 0 ? (
                                    <MonoValue value={unit.area} suffix="m²" size="sm" tone="muted" />
                                  ) : (
                                    <span className="text-xs text-gray-400">{t('sales.area_not_specified')}</span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </DesignFormField>
                  )}

                  {/* Sale Amount */}
                  {selectedUnit && (
                    <>
                      <DesignFormField label={t('sales.sale_amount')} required>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              id="amount"
                              type="number"
                              inputMode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              placeholder="0"
                              value={saleAmount}
                              onChange={(e) => setSaleAmount(e.target.value)}
                              className="bg-white border-gray-200 text-gray-900 rounded-md pr-8"
                            />
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-gray-400">
                              {currency === 'TRY' ? '₺' : currency}
                            </span>
                          </div>
                          <Select value={currency} onValueChange={setCurrency}>
                            <SelectTrigger className="w-24 bg-white border-gray-200 text-gray-900 rounded-md">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200 text-gray-900">
                              {currencyOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {saleAmount && !isNaN(parseFloat(saleAmount)) && (
                          <div className="mt-1.5 flex justify-end">
                            <MonoValue
                              value={parseFloat(saleAmount)}
                              format
                              suffix={currency === 'TRY' ? '₺' : ` ${currency}`}
                              tone="success"
                              size="sm"
                            />
                          </div>
                        )}
                      </DesignFormField>

                      {/* Date Selection */}
                      <DesignFormField label={t('sales.sale_date')}>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal border-gray-200 bg-white text-gray-900"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                              <span className="font-mono text-[13px]">{format(date, "dd MMM yyyy", { locale: tr })}</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={(selectedDate) => selectedDate && setDate(selectedDate)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </DesignFormField>

                      {/* Description Field */}
                      <DesignFormField label={t('sales.description')}>
                        <textarea
                          id="description"
                          placeholder={t('sales.description_placeholder')}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full min-h-[80px] px-3 py-2 text-sm bg-white text-gray-900 border border-gray-200 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-600 touch-manipulation placeholder:text-gray-400"
                          rows={3}
                        />
                      </DesignFormField>

                      {/* Buyer Information Section */}
                      <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div>
                          <h3 className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">{t('sales.buyer_info')}</h3>
                          <p className="text-xs text-gray-400 mt-0.5">{t('sales.buyer_info_description')}</p>
                        </div>

                        <DesignFormField label={t('sales.buyer_name')}>
                          <Input
                            id="buyerName"
                            placeholder={t('sales.buyer_name_placeholder')}
                            value={buyerName}
                            onChange={(e) => setBuyerName(e.target.value)}
                            className="bg-white border-gray-200 text-gray-900 rounded-md touch-manipulation mobile-touch-friendly"
                          />
                        </DesignFormField>

                        <DesignFormField label={t('sales.buyer_phone')}>
                          <Input
                            id="buyerPhone"
                            type="tel"
                            placeholder={t('sales.buyer_phone_placeholder')}
                            value={buyerPhone}
                            onChange={(e) => setBuyerPhone(e.target.value)}
                            className="bg-white border-gray-200 text-gray-900 rounded-md touch-manipulation mobile-touch-friendly font-mono"
                          />
                        </DesignFormField>
                      </div>

                      {/* Submit Button */}
                      <div className="pt-2">
                        <Button
                          type="submit"
                          className="w-full h-12 text-base font-semibold touch-manipulation bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white rounded-md"
                          disabled={loading || !selectedUnit || !saleAmount}
                        >
                          {loading ? t('sales.saving') : isEditing ? t('sales.update_sale') : t('sales.save_sale')}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </div>

              {/* Sold Units Display */}
              {selectedProject && soldUnits.filter(unit => unit.projectId === selectedProjectId).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-success-600" />
                    <h3 className="text-[13px] font-semibold text-gray-900">{t('sales.sold_units')}</h3>
                  </div>
                  <div className="space-y-2">
                    {soldUnits
                      .filter(unit => unit.projectId === selectedProjectId)
                      .map((unit, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-500">
                          <X className="h-3 w-3 text-error-600 shrink-0" />
                          <span>{unit.unitType === 'flat' ? t('sales.unit_type.flat') : t('sales.unit_type.shop')} {unit.unitNumber}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default SalePage; 